
import React, { useState, useEffect } from 'react'
import { compose } from 'widgets-shared/core'
import { withComponentContext } from 'widgets-shared/core'
import FixtureCard from '../FixtureCard/FixtureCard'

import { FixtureData, CompetitionData, OddsFormat } from './../types'
import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { MessageBusType } from 'smartstream-widgets/types/sharedModule/core'
import { homeFirstCompetitions } from 'smartstream-widgets/constants/shared'
import { Bookmakers } from 'smartstream-widgets/types/sharedModule/core'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'

type Props = {
  bookmakers: Bookmakers,
  classes: {
    [K in string]: string
  },
  data: Array<FixtureData & CompetitionData>,
  oddsFormat: OddsFormat,
  config: {
    usePrebid: boolean,
    advertInterval: number,
    advertIdSlotPrefix: string,
    maxAdvertsPerPage: number
  },
  messageBus: MessageBusType,
  competitionId: string,
  page: string,
  lastUpdateAction: string,
  isBookmakerLinkEnabled: boolean
}

const styles = (theme: Theme) =>  createStyles({
  root: {
    marginBottom: 20,
    background: theme.palette.background.paper
  },
  advertSlot: {
    textAlign: 'center',
    '& iframe': {
      marginBottom: 20
    }
  }
})

export function extractFixtures(data: Array<FixtureData & CompetitionData>): Array<FixtureData> {
  return data.reduce((acc: Array<FixtureData>, cp) => {
    const { fixtures } = cp
    return (fixtures && fixtures.length > 0) ? acc.concat(fixtures) : acc
  }, [])
}

export function addAdvertItems(
  fixtures: Array<FixtureData | string>,
  advertInterval: number,
  maxAdvertsPerPage: number
): Array<FixtureData | string> {
  let advertCount = 1
  const shouldRenderAdvertBefore = fixtures.length < advertInterval

  return fixtures.reduce((acc: Array<FixtureData | string>, elem, idx) => {
    const count = idx + 1
    const shouldAddSpace = (count % advertInterval) === 0
    acc.push(elem)
    if(shouldRenderAdvertBefore && advertCount <= maxAdvertsPerPage) {
      advertCount += maxAdvertsPerPage
      acc.push('advert')
    }
    if(shouldAddSpace && advertCount <= maxAdvertsPerPage) {
      advertCount += 1
      acc.push('advert')
    }
    return acc
  }, [])
}

export function sendMessage(messageBus: MessageBusType, slotsAvailable: number, action: string) {
  const body = {
    slotsAvailable,
    action
  }
  messageBus && messageBus.dispatchEvent('ocgm-adv:request', body)
}

function FixtureCardsList (Props: Props) {
  const {
    bookmakers = {},
    isBookmakerLinkEnabled,
    classes,
    data = [],
    competitionId,
    page,
    lastUpdateAction,
    oddsFormat,
    config: {
      usePrebid = false,
      advertInterval = 2,
      advertIdSlotPrefix = 'ocgm-adv',
      maxAdvertsPerPage = 2
    },
    messageBus
  } = Props
  const isByCompetition = data[0] && data[0].fixtures && data[0].fixtures.length > 0
  const fixturesData = isByCompetition ? extractFixtures(data) : data
  const fixturesWithAdvertItems: Array<FixtureData | string> = usePrebid ?
    addAdvertItems(fixturesData, advertInterval, maxAdvertsPerPage) : fixturesData
  const [slotsAvailable, setSlotsAvailable] = useState(0)
  const [currentTabId, setCurrentTabId] = useState(competitionId)
  const [currentPage, setCurrentPage] = useState(page)
  let advertCount = 0

  useEffect(() => {
    const advertsItemsLength = fixturesWithAdvertItems.filter(item => item === 'advert').length
    setSlotsAvailable(advertsItemsLength)

    if(lastUpdateAction !== 'render') {
      competitionId && setCurrentTabId(competitionId)
      page && setCurrentPage(page)
    }
  })

  useEffect(() => {
    if (usePrebid && messageBus && slotsAvailable > 0) sendMessage(messageBus, slotsAvailable, lastUpdateAction)
  }, [slotsAvailable, currentTabId, currentPage])

  return fixturesWithAdvertItems.map((fixture: FixtureData | string) => {
    const isAdvert = fixture === 'advert'
    if(isAdvert) advertCount += 1

    return (
      isAdvert ?
        <div
          data-testid="ocgm-advert-container"
          id={`${advertIdSlotPrefix}-${advertCount}`}
          key={`${advertIdSlotPrefix}-${advertCount}-${page}`}
          className={`ocgm-advert-container ${classes.advertSlot}`} />
      : <FixtureCard
        bookmakers={bookmakers}
        isBookmakerLinkEnabled={isBookmakerLinkEnabled}
        key={(fixture as FixtureData).fixtureId}
        classes={{ root: classes.root }}
        fixture={fixture}
        isAwayTeamFirst={!homeFirstCompetitions.includes((fixture as FixtureData).competitionId)}
        oddsFormat={oddsFormat}
        />
    )
  })
}

export default compose(
  withStyles(styles,{ defaultTheme: getDefaultTheme}),
  withComponentContext
)(FixtureCardsList)
